import { parseMd, plainText, splitImportTitle } from '../markdown/wikiMarkdown';
import { plural } from '../shared/wikiFormat';
import { WIKI_IMPORT_ERRORS, WIKI_IMPORT_WARNINGS } from './wikiModalCopy';

/**
 * Pure helpers for the New page modal's "Import a file" mode — the prototype's
 * `wkPickFile` / `wkSetImport` / `wkImpStats` / `wkImpPreview` (C2 §1.5, §1.9)
 * on top of the shared Markdown engine (`markdown/wikiMarkdown.js`), so the
 * preview counts are exactly what the editor and the read view will parse.
 */

export const WIKI_IMPORT_ACCEPT = '.md,.markdown,.mdown,.txt,.docx';
export const WIKI_IMPORT_EXTENSIONS = Object.freeze(['md', 'markdown', 'mdown', 'txt', 'docx']);
export const WIKI_IMPORT_MAX_BYTES = 6 * 1024 * 1024;
export const WIKI_IMPORT_PREVIEW_ROWS = 7;

/** Prototype: the text after the last "." lowercased (a name without a dot is its own "extension"). */
export function importExtension(name) {
    return (String(name || '').split('.').pop() || '').toLowerCase();
}

/** `'markdown' | 'text' | 'docx' | null` — the server's `kind` for an extension. */
export function importKind(name) {
    const ext = importExtension(name);
    if (ext === 'md' || ext === 'markdown' || ext === 'mdown') return 'markdown';
    if (ext === 'txt') return 'text';
    if (ext === 'docx') return 'docx';
    return null;
}

/** The prototype's client-side refusal (type first, then the 6 MB cap), or null. */
export function checkImportFile(file) {
    if (!file) return null;
    if (!WIKI_IMPORT_EXTENSIONS.includes(importExtension(file.name))) return WIKI_IMPORT_ERRORS.type;
    if (Number(file.size) > WIKI_IMPORT_MAX_BYTES) return WIKI_IMPORT_ERRORS.size;
    return null;
}

const HTML_RE = /<[a-z][\s\S]*?>/i;

/** Union of warning lists, first occurrence order. */
export function mergeWarnings(...lists) {
    const out = [];
    lists.forEach((list) => (list || []).forEach((warning) => {
        if (typeof warning === 'string' && warning && !out.includes(warning)) out.push(warning);
    }));
    return out;
}

/**
 * Prototype `wkSetImport(name, size, raw, extraWarn)`:
 *   CRLF → LF; a `# ` heading at the very start is the title (removed with the
 *   blank lines after it), else the file name with `-`/`_` → space; blocks =
 *   the shared engine's `parseMd(body)`; warnings: extra…, no heading, HTML.
 * A UTF-8 BOM is dropped first (the server strips it too).
 *
 * Not ported: "Only the first 4,000 blocks will be imported." — neither the
 * prototype nor the server truncates, so the warning would be false. A server
 * preview warning is merged in by the caller if the server ever adds it.
 */
export function parseImportText(name, size, raw, extraWarnings = []) {
    const text = String(raw == null ? '' : raw).replace(/^\uFEFF/, '');
    const { title, body, hasHeading } = splitImportTitle(text, name);
    const blocks = parseMd(body);
    const warnings = mergeWarnings(extraWarnings);
    if (!hasHeading) warnings.push(WIKI_IMPORT_WARNINGS.noHeading);
    if (HTML_RE.test(body)) warnings.push(WIKI_IMPORT_WARNINGS.html);
    return { name, size, title, raw: body, blocks, warnings: mergeWarnings(warnings) };
}

/** Prototype `wkImpSize`: "705 bytes" under 1 KB, else one-decimal KB (even for MB-size files). */
export function formatImportSize(size) {
    const n = Number(size) || 0;
    if (!n) return '';
    return n < 1024 ? `${n} bytes` : `${Math.round(n / 102.4) / 10} KB`;
}

const STAT_LABELS = Object.freeze({
    h1: 'heading', h2: 'heading', h3: 'heading', p: 'paragraph', li: 'list item', num: 'step', check: 'to-do',
    callout: 'callout', quote: 'quote', code: 'code block', table: 'table', image: 'image', divider: 'divider',
    embed: 'video', toggle: 'toggle',
});

/**
 * Prototype `wkImpStats`: "{N} blocks · {count} {label}[s] · …", per-type
 * counts in first-appearance order (h1/h2/h3 share "heading").
 */
export function importStatsLabel(blocks) {
    const list = Array.isArray(blocks) ? blocks : [];
    const byType = new Map();
    list.forEach((block) => byType.set(block.t, (byType.get(block.t) || 0) + 1));
    const byLabel = new Map();
    byType.forEach((count, type) => {
        const label = STAT_LABELS[type] || type;
        byLabel.set(label, (byLabel.get(label) || 0) + count);
    });
    const parts = [plural(list.length, 'block')];
    byLabel.forEach((count, label) => parts.push(plural(count, label)));
    return parts.join(' · ');
}

const TYPE_LABELS = Object.freeze({
    h1: 'Heading', h2: 'Heading', h3: 'Heading', p: 'Text', li: 'Bullet', num: 'Step', check: 'To-do',
    callout: 'Callout', quote: 'Quote', code: 'Code', table: 'Table', image: 'Image', divider: 'Divider',
    embed: 'Video', toggle: 'Toggle',
});

/**
 * Prototype `wkImpPreview`: the first 7 blocks as `{type, text}` — tables as
 * "{head joined ' | '}  ({rows} rows)" (two spaces), dividers "—", everything
 * else its inline-stripped text (or the URL for images/videos).
 */
export function importPreviewRows(blocks, limit = WIKI_IMPORT_PREVIEW_ROWS) {
    return (Array.isArray(blocks) ? blocks : []).slice(0, limit).map((block) => {
        let text;
        if (block.t === 'table') {
            text = `${(block.head || []).join(' | ')}  (${plural((block.rows || []).length, 'row')})`;
        } else if (block.t === 'divider') {
            text = '—';
        } else {
            text = plainText(block.text || '') || block.url || '';
        }
        return { type: TYPE_LABELS[block.t] || block.t, text };
    });
}

/** "and {N-7} more" when there are more than 7 blocks, else ''. */
export function importMoreLabel(blocks, limit = WIKI_IMPORT_PREVIEW_ROWS) {
    const n = Array.isArray(blocks) ? blocks.length : 0;
    return n > limit ? `and ${n - limit} more` : '';
}

/**
 * Merge a `POST /wiki/import/preview` response into the import state:
 * the token, the server's warnings, and — when the server's stored body is
 * not what the browser parsed (or there was no browser parse: .docx) — the
 * blocks are re-parsed from the server body, so the preview, the stats and
 * the toast count describe exactly the page that commit will create.
 */
export function applyServerPreview(state, preview, token) {
    if (!state || state.error) return state;
    const body = typeof preview?.body_md === 'string' ? preview.body_md : null;
    const next = { ...state, token, uploading: false, pending: false };
    if (body != null && (!Array.isArray(state.blocks) || body !== state.raw)) {
        next.raw = body;
        next.blocks = parseMd(body);
    }
    if (!next.title) next.title = preview?.title || '';
    next.warnings = mergeWarnings(state.warnings, preview?.warnings);
    return next;
}
